import { Position } from '../../core/types';
import {
  DoubleSide,
  LinearFilter,
  MeshBasicMaterial,
  MeshLambertMaterial,
  Texture,
  Vector2,
} from 'three';
import * as _ from 'lodash';

const textMaterialCache = new Map<string, MeshBasicMaterial[] | MeshLambertMaterial[]>();

export function clearTextMaterialCache(): void {
  textMaterialCache.clear();
}

function getTextMaterialCacheKey(
  width: number,
  height: number,
  length: number,
  units: string,
  color: number,
  nameTranslated: string,
  canRotate: boolean | undefined,
  isShadows: boolean | undefined,
  isPreview: boolean | undefined,
  loadType: number | undefined,
): string {
  return [
    width,
    height,
    length,
    units,
    color,
    nameTranslated,
    canRotate ? 1 : 0,
    isShadows ? 1 : 0,
    isPreview ? 1 : 0,
    loadType ?? 0,
  ].join('|');
}

function calculateBoxCanvasSize(
  canvas: HTMLCanvasElement,
  length: number,
  height: number,
  units: string,
): boolean {
  if (height > length) {
    canvas.width = calcSize(height, units);
    canvas.height = calcSize(length, units);
    return true;
  } else {
    canvas.width = calcSize(length, units);
    canvas.height = calcSize(height, units);
    return false;
  }
}

function generateCubicTextMaterials(
  width: number,
  height: number,
  length: number,
  units: string,
  color: number,
  nameTranslated: string,
  canRotate?: boolean,
  isShadows?: boolean,
  centerOfMass?: Position,
  isPreview?: boolean,
  loadType?: number,
  document?: Document,
): MeshBasicMaterial[] | MeshLambertMaterial[] {
  const topCanvas: HTMLCanvasElement = document.createElement('canvas');
  const topCanvasRotate: boolean = calculateBoxCanvasSize(topCanvas, length, width, units);
  createTextContext(topCanvas, color, nameTranslated, units, length, width, width, false, loadType);
  const topCanvasTexture: Texture = createTextTexture(
    topCanvas,
    loadType,
    !topCanvasRotate,
    canRotate,
    true,
  );
  const leftCanvas: HTMLCanvasElement = document.createElement('canvas');
  const leftCanvasRotate: boolean = calculateBoxCanvasSize(leftCanvas, length, height, units);
  createTextContext(leftCanvas, color, nameTranslated, units, width, height, length, false, loadType);
  const leftCanvasTexture: Texture = createTextTexture(
    leftCanvas,
    loadType,
    leftCanvasRotate,
    canRotate,
  );
  const frontCanvas: HTMLCanvasElement = document.createElement('canvas');
  const frontCanvasRotate: boolean = calculateBoxCanvasSize(frontCanvas, width, height, units);
  createTextContext(frontCanvas, color, nameTranslated, units, width, height, length, false, loadType);
  const frontCanvasTexture: Texture = createTextTexture(
    frontCanvas,
    loadType,
    frontCanvasRotate,
    canRotate,
  );
  const materialsLambert: MeshLambertMaterial[] = [
    new MeshLambertMaterial({
      map: leftCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshLambertMaterial({
      map: leftCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshLambertMaterial({
      map: topCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshLambertMaterial({
      map: topCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshLambertMaterial({
      map: frontCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshLambertMaterial({
      map: frontCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
  ];

  const materialsBasic: MeshBasicMaterial[] = [
    new MeshBasicMaterial({
      map: leftCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshBasicMaterial({
      map: leftCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshBasicMaterial({
      map: topCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshBasicMaterial({
      map: topCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshBasicMaterial({
      map: frontCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
    new MeshBasicMaterial({
      map: frontCanvasTexture,
      transparent: false,
      side: DoubleSide,
    }),
  ];
  if (centerOfMass && isPreview) {
    return isShadows
      ? [new MeshLambertMaterial({ map: null, transparent: true, opacity: 0 })]
      : [new MeshBasicMaterial({ map: null, transparent: true, opacity: 0 })];
  }
  return isShadows ? materialsLambert : materialsBasic;
}

function generateCylindricalTextMaterials(
  width: number,
  height: number,
  length: number,
  units: string,
  color: number,
  nameTranslated: string,
  canRotate?: boolean,
  isShadows?: boolean,
  centerOfMass?: Position,
  isPreview?: boolean,
  loadType?: number,
  document?: Document,
): MeshBasicMaterial[] | MeshLambertMaterial[] {
  let rotate: boolean = undefined;
  const canvas: HTMLCanvasElement = document.createElement('canvas');
  if (loadType === 1) {
    const barrelWidth: number = width / 2;
    if (barrelWidth >= height) {
      canvas.width = calcSize(width, units);
      canvas.height = calcSize(height, units);
      rotate = false;
    }
    if (height >= barrelWidth) {
      canvas.width = calcSize(height, units);
      canvas.height = calcSize(width, units);
      rotate = true;
    }
  } else if (loadType === 2) {
    canvas.width = calcSize(width, units);
    canvas.height = calcSize(height, units);
    rotate = true;
  }
  if (loadType === 1 || loadType === 2) {
    const isWider: boolean =
      (loadType === 1 && width / 2 > height) || (loadType === 2 && width > height);
    let textureTop: Texture;
    let texture: Texture;
    if (isWider && loadType === 1) {
      const canvas2: HTMLCanvasElement = document.createElement('canvas');
      canvas2.width = calcSize(width, units);
      canvas2.height = calcSize(width, units);
      createTextContext(canvas2, color, nameTranslated, units, width, width, length, true, loadType);
      textureTop = createTextTexture(canvas2, loadType, rotate, canRotate, true, isWider);

      const canvas3: HTMLCanvasElement = document.createElement('canvas');
      const textureWidth2: number = width + width * 1.5;
      canvas3.width = calcSize(textureWidth2, units);
      canvas3.height = calcSize(height, units);
      createTextContext(canvas3, color, nameTranslated, units, width, height, length, false, loadType);
      texture = createTextTexture(canvas3, loadType, rotate, canRotate, false, isWider);
    } else {
      createTextContext(canvas, color, nameTranslated, units, width, height, length, false, loadType);
      textureTop = createTextTexture(canvas, loadType, rotate, canRotate, true, isWider);
      texture = createTextTexture(canvas, loadType, rotate, canRotate, false, isWider);
    }
    if (centerOfMass && isPreview) {
      return isShadows
        ? [new MeshLambertMaterial({ map: null, transparent: true, opacity: 0 })]
        : [new MeshBasicMaterial({ map: null, transparent: true, opacity: 0 })];
    }

    const materialsLambert: MeshLambertMaterial[] =
      loadType === 1
        ? [
            new MeshLambertMaterial({
              map: texture,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshLambertMaterial({
              map: textureTop,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshLambertMaterial({
              map: textureTop,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshLambertMaterial({
              map: texture,
              transparent: false,
              side: DoubleSide,
            }),
          ]
        : [new MeshLambertMaterial({ map: texture, transparent: false, side: DoubleSide })];

    const materialsBasic: MeshBasicMaterial[] =
      loadType === 1
        ? [
            new MeshBasicMaterial({
              map: texture,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshBasicMaterial({
              map: textureTop,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshBasicMaterial({
              map: textureTop,
              transparent: false,
              side: DoubleSide,
            }),
            new MeshBasicMaterial({
              map: texture,
              transparent: false,
              side: DoubleSide,
            }),
          ]
        : [new MeshBasicMaterial({ map: texture, transparent: false, side: DoubleSide })];
    return isShadows ? materialsLambert : materialsBasic;
  }
}

export function generateTextMaterials(
  width: number,
  height: number,
  length: number,
  units: string,
  color: number,
  nameTranslated: string,
  canRotate?: boolean,
  isShadows?: boolean,
  centerOfMass?: Position,
  isPreview?: boolean,
  loadType?: number,
  document?: Document,
): MeshBasicMaterial[] | MeshLambertMaterial[] {
  const cacheKey = getTextMaterialCacheKey(
    width,
    height,
    length,
    units,
    color,
    nameTranslated,
    canRotate,
    isShadows,
    isPreview,
    loadType,
  );
  const cached = textMaterialCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  let materials: MeshBasicMaterial[] | MeshLambertMaterial[];
  if (loadType === 0) {
    materials = generateCubicTextMaterials(
      width,
      height,
      length,
      units,
      color,
      nameTranslated,
      canRotate,
      isShadows,
      centerOfMass,
      isPreview,
      loadType,
      document,
    );
  } else if (loadType === 1 || loadType === 2) {
    materials = generateCylindricalTextMaterials(
      width,
      height,
      length,
      units,
      color,
      nameTranslated,
      canRotate,
      isShadows,
      centerOfMass,
      isPreview,
      loadType,
      document,
    );
  }

  if (materials) {
    textMaterialCache.set(cacheKey, materials);
  }
  return materials;
}

function createTextContext(
  canvas: HTMLCanvasElement,
  color: number,
  nameTranslated: string,
  units: string,
  width: number,
  height: number,
  length: number,
  isTop = false,
  loadType = 0,
): void {
  const textContext: CanvasRenderingContext2D = canvas.getContext('2d');
  const MAX_WIDTH: number = _.round(canvas.width - canvas.width * 0.1);
  const words: string[] = nameTranslated.split(' ');
  const xLine: number = _.round(canvas.width * 0.5);
  let yLine: number = _.round(canvas.height * 0.5);
  let line = '';
  let lineNumbers = 0;
  let lineTestForNumbers = '';
  const r: number = (color >> 16) & 0xff;
  const g: number = (color >> 8) & 0xff;
  const b: number = (color >> 0) & 0xff;
  textContext.fillStyle = `rgb(${r},${g},${b})`;
  textContext.fillRect(0, 0, canvas.width, canvas.height);
  if (width >= 1500 && height >= 1500 && length >= 1500) {
    const luma: number = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    textContext.fillStyle = luma < 190 ? '#ffffff' : '#191919';
    const dimensions = {
      width,
      height,
      length,
    };
    let LETTER_HEIGHT = 38;
    const isLb = units === 'lb';
    let textSize: string;
    switch (true) {
      case loadType === 1 && !isTop && dimensions.width / 2 >= dimensions.height:
        textSize = isLb ? '76px Roboto' : '38px Roboto';
        LETTER_HEIGHT = isLb ? 76 : 32;
        break;
      case isTop && loadType === 1 && dimensions.width <= 2500:
        textSize = isLb ? '64px Roboto' : '30px Roboto';
        LETTER_HEIGHT = isLb ? 64 : 30;
        break;
      case isTop && loadType === 1 && dimensions.width <= 10000:
        textSize = isLb ? '64px Roboto' : '38px Roboto';
        LETTER_HEIGHT = isLb ? 64 : 38;
        break;
      case isTop && dimensions.width > 10000:
        textSize = isLb ? '64px Roboto' : '76px Roboto';
        LETTER_HEIGHT = isLb ? 64 : 76;
        break;
      case dimensions.width <= 3500 || dimensions.height <= 3500 || dimensions.length <= 3500:
        textSize = isLb ? '26px Roboto' : '22px Roboto';
        LETTER_HEIGHT = isLb ? 26 : 26;
        break;
      case dimensions.width <= 5000 || dimensions.height <= 5000 || dimensions.length <= 5000:
        textSize = isLb ? '32px Roboto' : '25px Roboto';
        LETTER_HEIGHT = isLb ? 32 : 25;
        break;
      case dimensions.width <= 10000 || dimensions.height <= 10000 || dimensions.length <= 10000:
        textSize = isLb ? '62px Roboto' : '25px Roboto';
        LETTER_HEIGHT = isLb ? 62 : 25;
        break;
      default:
        textSize = isLb ? '76px Roboto' : '32px Roboto';
        LETTER_HEIGHT = isLb ? 76 : 33;
    }

    textContext.font = textSize;
    textContext.textAlign = 'center';

    for (let i = 0; i < words.length; i++) {
      const testLine: string = lineTestForNumbers + words[i] + ' ';
      const metrics: TextMetrics = textContext.measureText(testLine);

      if (metrics.width > MAX_WIDTH && i > 0) {
        lineTestForNumbers = words[i] + ' ';
        lineNumbers += 1;
      } else {
        lineTestForNumbers = testLine;
      }
    }

    const lineStart: number = (lineNumbers * LETTER_HEIGHT) / 2;
    if (lineNumbers > 0) {
      yLine -= lineStart;
    }

    yLine = yLine < LETTER_HEIGHT ? LETTER_HEIGHT : yLine;

    for (let n = 0; n < words.length; n++) {
      const testLine: string = line + words[n] + ' ';
      const metrics: TextMetrics = textContext.measureText(testLine);
      const testWidth: number = metrics.width;

      if (testWidth > MAX_WIDTH && n > 0) {
        textContext.fillText(line, xLine, yLine, MAX_WIDTH);
        line = words[n] + ' ';
        yLine += LETTER_HEIGHT;
      } else {
        line = testLine;
      }
    }
    textContext.fillText(line, xLine, yLine, MAX_WIDTH);
  }
}

function createTextTexture(
  canvas: HTMLCanvasElement,
  loadType: number,
  rotate: boolean,
  canRotate: boolean,
  isTop = false,
  isWider = false,
): Texture {
  const texture: Texture = new Texture(canvas);
  texture.center.x = 0.5;
  texture.center.y = 0.5;
  if (loadType === 1 && !isTop) {
    if (!isWider) {
     texture.offset = new Vector2(0, 0.29);
    } else {
      texture.offset = new Vector2(-0.245, 0.1);
    }
  }

  if (loadType === 2 && !isTop) {
    texture.offset = new Vector2(0, 0.27);
  }

  const isNotBarrelOrPipe: boolean = loadType !== 1 && !isTop && loadType !== 2;
  if (rotate && canRotate) {
    texture.rotation = Math.PI / -2;
  } else if (rotate) {
    texture.rotation = isTop ? Math.PI / -2 : Math.PI / 2;
    if (isTop) {
      texture.offset = new Vector2(0, 0.05);
    }
    if (isNotBarrelOrPipe) {
      texture.offset = new Vector2(0, 0.1);
    }
  } else if (isNotBarrelOrPipe) {
    texture.offset = new Vector2(0, 0.05);
  }
  if (loadType === 1 && isTop && !isWider) {
    texture.rotation = 2 * Math.PI;
    texture.offset = new Vector2(-0.03, 0.05);
  }
  if (loadType === 1 && isTop && isWider) {
    texture.offset = new Vector2(-0.03, 0.05);
  }
  texture.minFilter = LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

const INTERLAYER_MATERIAL_OPACITY = 0.45;

const interlayerMaterialOptions = {
  transparent: true,
  opacity: INTERLAYER_MATERIAL_OPACITY,
  side: DoubleSide,
} as const;

/** Semi-transparent solid material for pallet interlayers. */
export function generateInterlayerStripeMaterials(
  color: number,
  isShadows?: boolean,
): MeshBasicMaterial[] | MeshLambertMaterial[] {
  if (isShadows) {
    return [new MeshLambertMaterial({ color, ...interlayerMaterialOptions })];
  }
  return [new MeshBasicMaterial({ color, ...interlayerMaterialOptions })];
}

function calcSize(dimension: number, units: string): number {
  if (units === 'lb') {
    return _.round(dimension / 20);
  } else {
    return _.round(dimension / 50);
  }
}
